import {
  ADMIN_DELETE_ITEM,
  fetcher,
  fetchWithAuth,
  ITEM_URL,
  resolvePayload,
} from "./const.routes.ts";
import useSWR from "swr";
import { CategoryParam } from "../types/ItemQuery";
import { ERROR, ErrorType, parseError } from "../types/Error/Errors.ts";
import { ItemPostType, ItemPutType, ItemType } from "../types/Item.ts";
import { err, Result } from "../types/Result.ts";
import { ROOT_SLUG } from "../const/enviroment.ts";

export function useItem(id: string, fallback: ItemType) {
  const { data, error, isLoading } = useSWR<ItemType, Error>(
    ITEM_URL + id,
    fetcher,
    { keepPreviousData: true, refreshInterval: 0, dedupingInterval: 50_000 },
  );

  return {
    item: data ? data : fallback,
    isLoading,
    isError: error && ERROR.ITEM_NOT_FOUND,
  };
}

export async function initItem(): Promise<Result<ItemType, ErrorType>> {
  try {
    const response = await fetchWithAuth(ITEM_URL + "init", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category: ROOT_SLUG }),
    });
    return await resolvePayload(response);
  } catch (e) {
    return err(parseError(e));
  }
}

export function useItemAmount(id: string, fallback: number) {
  const { data, error, isLoading } = useSWR<number, Error>(
    `${ITEM_URL}count/${id}`,
    fetcher,
    { keepPreviousData: true, refreshInterval: 0 },
  );

  return {
    amountItems: data ? data : fallback,
    isLoading,
    isError: error && ERROR.ITEM_NOT_FOUND,
  };
}

export type ItemCountProps = {
  categorySlug: string;
  filter?: string;
  status?: string;
  user?: string;
  search?: string;
  fallback?: number;
};

export function useItemsCount({
  categorySlug,
  filter,
  status,
  user,
  search,
  fallback,
}: ItemCountProps) {
  // Ensure base URL
  let url = `${ITEM_URL}count`;

  // Build query parameters
  const queryParams: Record<string, string> = {};

  if (categorySlug) {
    queryParams.category = categorySlug;
  }
  if (filter) {
    queryParams.filter = filter.replace(/^,+|,+$/g, "");
  }

  if (status) {
    queryParams.status = status;
  }

  if (user) {
    queryParams.user = user;
  }
  const queryString = new URLSearchParams(queryParams).toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  if (search?.trim()) {
    queryParams.search = search;
  }

  const { data, error, isLoading } = useSWR<number, Error>(url, fetcher);
  return {
    count: data ? data : fallback || 0,
    isLoading,
    isError: error,
  };
}

export function useSmartOptions(
  categoryId: string,
  filter: string,
  fallback: string[],
) {
  // Ensure base URL
  let url = `${ITEM_URL}filter`;

  // Build query parameters
  const queryParams: Record<string, string> = {};

  if (categoryId) {
    queryParams.category = categoryId;
  }
  if (filter.trim()) {
    queryParams.filter = filter.replace(/^,+|,+$/g, "");
  }

  const queryString = new URLSearchParams(queryParams).toString();
  if (queryString) {
    url += `?${queryString}`;
  }
  const { data, error, isLoading } = useSWR<string[] | number[], Error>(
    url,
    fetcher,
  );
  return {
    options: data ? data : fallback || [],
    isLoading,
    isError: error,
  };
}

export function useItems(queryParam: CategoryParam) {
  const {
    categorySlug,
    page,
    pageSize,
    filter,
    sortBy,
    sortOrder,
    user,
    status,
    search,
  } = queryParam;
  // Ensure base URL
  let url = `${ITEM_URL}items`;

  // Build query parameters
  const queryParams: Record<string, string> = {};
  queryParams.category = categorySlug;

  if (page) {
    queryParams.page = String(page);
  }

  if (pageSize) {
    queryParams.pageSize = String(pageSize);
  }
  if (filter?.trim()) {
    queryParams.filter = filter.replace(/^,+|,+$/g, "");
  }

  if (sortBy?.trim()) {
    queryParams.sort = sortBy;
  }

  if (sortOrder?.trim()) {
    queryParams.order = sortOrder;
  }

  if (user?.trim()) {
    queryParams.user = user;
  }

  if (status?.trim()) {
    queryParams.status = status;
  }

  if (search?.trim()) {
    queryParams.search = search;
  }

  const queryString = new URLSearchParams(queryParams).toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  const { data, error, isLoading, isValidating } = useSWR<string[], Error>(
    url,
    fetcher,
    { keepPreviousData: true, refreshInterval: 0, dedupingInterval: 10_000 },
  );

  return {
    itemIds: data ? data : [],
    isLoading,
    isValidating,
    isError: error,
  };
}

export async function postProduct(
  product: ItemPostType,
): Promise<Result<ItemType, ErrorType>> {
  try {
    const response = await fetchWithAuth(ITEM_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(product),
    });
    return await resolvePayload(response);
  } catch (e) {
    return err(parseError(e));
  }
}

export async function putProduct(
  product: ItemPutType,
): Promise<Result<ItemType, ErrorType>> {
  try {
    const response = await fetchWithAuth(ITEM_URL, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(product),
    });
    return await resolvePayload(response);
  } catch (e) {
    return err(parseError(e));
  }
}

export async function adminDeleteItem(
  itemId: string,
  password: string,
): Promise<Result<ItemType, ErrorType>> {
  try {
    const response = await fetchWithAuth(ADMIN_DELETE_ITEM(itemId), {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password: password }),
    });
    return await resolvePayload(response);
  } catch (e) {
    return err(parseError(e));
  }
}
